import React, { useEffect, useRef } from "react";

// Decorative auto-looping background video: muted inline autoplay with poster fallback.
export function HeroVideo({ base, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // React sets `muted` as a JS property only — force the attribute so mobile
    // autoplay policies (and prerendered HTML) see a genuinely muted video.
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    v.playsInline = true;

    // Deterministic mobile source: `media` on <source> is unreliable across browsers.
    if (window.matchMedia("(max-width: 767px)").matches && !v.currentSrc.includes("-720p")) {
      v.src = `${base}-720p.mp4`;
      v.load();
    }

    const tryPlay = () => {
      if (v.paused) v.play().catch(() => {});
    };
    tryPlay();

    // Retry on first interaction and when the tab becomes visible
    // (covers iOS Low Power Mode / data saver, which block the initial attempt).
    const onInteract = () => tryPlay();
    const onVisible = () => { if (!document.hidden) tryPlay(); };
    v.addEventListener("canplay", tryPlay);
    window.addEventListener("touchstart", onInteract, { once: true, passive: true });
    window.addEventListener("pointerdown", onInteract, { once: true, passive: true });
    document.addEventListener("visibilitychange", onVisible);

    const io = new IntersectionObserver(
      ([e]) => (e.isIntersecting ? tryPlay() : v.pause()),
      { threshold: 0.1 }
    );
    io.observe(v);

    return () => {
      io.disconnect();
      v.removeEventListener("canplay", tryPlay);
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("pointerdown", onInteract);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [base]);

  return (
    <video
      ref={ref}
      className={`absolute inset-0 w-full h-full object-cover pointer-events-none motion-reduce:hidden ${className}`}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster={`${base}-poster.jpg`}
      aria-hidden="true"
      tabIndex={-1}
      disablePictureInPicture
      disableRemotePlayback
      data-testid="hero-video"
    >
      <source src={`${base}-720p.mp4`} type="video/mp4" media="(max-width: 767px)" />
      <source src={`${base}.mp4`} type="video/mp4" />
    </video>
  );
}
